import { Button } from '@eventbrite/eds-button';
import { Icon } from '@eventbrite/eds-icon';
import { NegativeIconFill } from '@eventbrite/eds-iconography';
import { LoadingOverlay } from '@eventbrite/eds-loading-overlay';
import { TYPE_ERROR } from '@eventbrite/eds-notification';
import { formValidate } from '@eventbrite/eds-utils';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import { logEvent } from '@eventbrite/statsig';
import { $FixMe } from '@eventbrite/ts-utils';
import loadable from '@loadable/component';
import isEmpty from 'lodash/isEmpty';
import React from 'react';
import { FooterError } from '../../components/FooterError';
import { FullPageLoadingLayer } from '../../components/fullPageLoadingLayer/FullPageLoadingLayer';
import {
    default as TicketCategoryGroup,
    TicketClassGroupProps,
} from '../../components/ticketsSelectionList/TicketCategoryGroup/TicketCategoryGroup';
import { POST_MESSAGE_STATUS, TICKETS_PAGE_FORM_NAME } from '../../constants';
import { FOCUSABLE_FORM_ELEMENTS } from '../../constants/focusableElements';
import { STATSIG_EVENT_NAMES } from '../../constants/statsig';
import { resetFocusToInitialElement } from '../../utils/accessibility';
import { postMessage as postMessageUtil } from '../../utils/postMessage';
import { isApplePayOnListingsExperimentEnabled } from '../../utils/statsig';
import './TicketSelectionCompactSizePage.scss';
import validate from './validation';

const INITIAL_PAGE_HEIGHT = 204;
const ConnectedExpressCheckout = loadable(
    () => import('../../components/ExpressCheckout/ConnectedExpressCheckout'),
);

export interface TicketSelectionCompactSizePageProps {
    errors: $FixMe[];
    getCanMakePaymentsWithActiveCard?: boolean;
    isDisabled: boolean;
    isExpressApplePayEnabled?: boolean;
    isFreeEvent: boolean;
    isLoadingApplePay?: boolean;
    onPageWillMount: () => void;
    onSubmit: (values: Record<string, unknown>) => void;
    /**
     * A common sales start or end date for all tickets, if it exists
     */
    salesDateForAllTickets?: GenericLazyString | string;
    /**
     * Whether to show the headings for ticket class category groupings
     */
    shouldShowTicketCategoryGroupHeaders: boolean;
    /**
     * A sales status (sales end soon, sales not started, etc.) that applies to all tickets, if it exists
     */
    statusForAllTickets?: string;
    /**
     * Props related to the checkout template
     */
    templateProps: {
        isReservedSeating: boolean;
        totalTicketQuantity: number;
        isSubmitting: boolean;
    };
    /**
     * Ticket classes sorted by category
     */
    ticketClassesByCategory: Record<string, TicketClassGroupProps>;
    /**
     * The total cost to display in the footer.
     */
    totalCostDisplay: GenericLazyString | string;
    /**
     * Node displaying any validation error next to the submit button.
     * Note that this node will be rendered only when the form has been submitted at least once.
     */
    validationErrorMessage: GenericLazyString | string;
    /**
     * Denotes that this app is being rendered inside an iframe, giving the url of its parent page
     */
    widgetParentUrl: string;

    /**
     * Props passed by redux-form
     */
    error: GenericLazyString | string;
    handleSubmit: (
        callback: (values: Record<string, unknown>) => void,
    ) => () => void;
    submitFailed: boolean;
}

class TicketSelectionCompactSizePage extends React.Component<
    TicketSelectionCompactSizePageProps,
    {
        hasSubmittedForm: boolean;
        isLoading: boolean;
        pageHeight: number;
    }
> {
    focusRef = React.createRef<HTMLDivElement>();
    pageWrapperRef = React.createRef<HTMLDivElement>();
    resizeObserver: ResizeObserver | null = null;

    constructor(props: TicketSelectionCompactSizePageProps) {
        super(props);

        this.state = {
            isLoading: false,
            hasSubmittedForm: false,
            pageHeight: INITIAL_PAGE_HEIGHT,
        };
    }

    UNSAFE_componentWillMount() {
        this.props.onPageWillMount();
    }

    componentDidMount() {
        const { widgetParentUrl } = this.props;

        if (widgetParentUrl) {
            this._pageLoadedPostMessage();
            this.resizeObserver = new ResizeObserver((entries) => {
                this._updatePageHeight(entries[0].target.clientHeight);
            });

            if (this.pageWrapperRef.current) {
                this.resizeObserver.observe(this.pageWrapperRef.current);
            }
        }
    }

    componentWillUnmount() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    _onSubmit(data = {}) {
        // TODO: Check what tracking is needed for our Ticket Selection experiment
        logEvent(STATSIG_EVENT_NAMES.TICKET_STEP_CONTINUE_TO_PAYMENT_CLICK);

        if (!this.state.hasSubmittedForm && this.props.validationErrorMessage) {
            logEvent(STATSIG_EVENT_NAMES.TICKET_STEP_VALIDATION_ERROR_VIEW);
        }
        this.setState({
            hasSubmittedForm: true,
        });

        if (this.props.validationErrorMessage && this.focusRef.current) {
            resetFocusToInitialElement({
                rootEl: this.focusRef.current,
                focusableEls: FOCUSABLE_FORM_ELEMENTS,
            });
            this.focusRef.current.scrollIntoView(false);
            return;
        }

        this.props.handleSubmit((values) =>
            this.props.onSubmit({
                ...values,
                ...data,
            }),
        )();
    }

    _getNotificationOptions() {
        const { error: submitError, errors, submitFailed } = this.props;

        const notificationOptions = [];

        if (!isEmpty(errors)) {
            errors.forEach((error: $FixMe) => {
                const errorDescription = error.errorDescription;

                notificationOptions.push({
                    type: TYPE_ERROR,
                    children: errorDescription,
                });
            });
        }

        if (submitFailed && submitError) {
            notificationOptions.push({
                type: TYPE_ERROR,
                children: submitError,
            });
        }

        return notificationOptions;
    }

    _pageHeightPostMessage(height: number) {
        const { widgetParentUrl } = this.props;

        postMessageUtil({
            messageName: POST_MESSAGE_STATUS.ticketSelectionResize,
            additionalData: { contentHeight: height },
            widgetParentUrl,
        });
    }

    _pageLoadedPostMessage() {
        const { widgetParentUrl } = this.props;

        postMessageUtil({
            messageName: POST_MESSAGE_STATUS.ticketSelectionCompactLoaded,
            widgetParentUrl,
        });
    }

    _updatePageHeight(currentPageHeight: number) {
        if (this.state.pageHeight !== currentPageHeight) {
            this.setState({ pageHeight: currentPageHeight });
            this._pageHeightPostMessage(currentPageHeight);
        }
    }

    render() {
        const {
            isDisabled,
            isExpressApplePayEnabled,
            getCanMakePaymentsWithActiveCard,
            isFreeEvent,
            templateProps,
            totalCostDisplay,
            validationErrorMessage,
            isLoadingApplePay,
        } = this.props;

        const { isLoading } = this.state;
        const ctaText = isFreeEvent
            ? gettext('Reserve a spot')
            : gettext('Check out for %(price)s', {
                  price: totalCostDisplay,
              });

        const errorNotifications = this._getNotificationOptions();

        const errorNotificationsNode =
            errorNotifications.length > 0 ? (
                <div className="eds-l-mar-vert-4">
                    <p className="eds-text-bm eds-text-color--error eds-align--center-vertical">
                        <span className="eds-l-mar-right-1">
                            <Icon type={<NegativeIconFill />} color="ui-red" />
                        </span>
                        {errorNotifications.map((err, i) => (
                            <span key={i}>{err.children}</span>
                        ))}
                    </p>
                </div>
            ) : null;

        const ticketClassCategories = Object.keys(
            this.props.ticketClassesByCategory,
        );

        const onSubmit = this._onSubmit.bind(this);

        const footerMessageNode =
            this.state.hasSubmittedForm && validationErrorMessage ? (
                <div className="eds-l-mar-vert-4">
                    <FooterError>{validationErrorMessage}</FooterError>
                </div>
            ) : null;
        const checkoutButtons =
            isExpressApplePayEnabled &&
            isApplePayOnListingsExperimentEnabled() ? (
                <ConnectedExpressCheckout
                    handleSubmit={this._onSubmit.bind(this)}
                    isSubmitting={templateProps.isSubmitting}
                />
            ) : (
                <Button
                    onClick={onSubmit}
                    style="fill"
                    disabled={isDisabled}
                    data-spec="eds-modal__primary-button"
                    data-automation="eds-modal__primary-button"
                    isLoading={templateProps.isSubmitting}
                    size="block"
                >
                    {ctaText}
                </Button>
            );

        const fullPageLoadingLayer = isExpressApplePayEnabled ? (
            <FullPageLoadingLayer
                isShown={templateProps.isSubmitting && isLoadingApplePay}
            >
                <div
                    className="eds-text-bl eds-mar-top-5"
                    data-testid="compact-size-loading-text"
                >
                    {getCanMakePaymentsWithActiveCard
                        ? gettext('Placing order')
                        : ''}
                </div>
            </FullPageLoadingLayer>
        ) : null;

        return (
            <>
                {fullPageLoadingLayer}
                <section
                    className="ticket-selection-compact"
                    data-spec="ticket-selection-compact"
                    ref={this.pageWrapperRef}
                >
                    <div ref={this.focusRef} data-spec="focus-ref" />
                    <form onSubmit={onSubmit} className="eds-l-mar-bot-4">
                        {ticketClassCategories.map((groupKey, i) => (
                            <TicketCategoryGroup
                                key={i}
                                ticketClassGroupProps={
                                    this.props.ticketClassesByCategory[groupKey]
                                }
                                {...this.props}
                            />
                        ))}
                    </form>
                    <LoadingOverlay
                        data-spec="spinner"
                        size="large-thin"
                        isShown={isLoading}
                    />
                    {errorNotificationsNode || footerMessageNode}
                    {checkoutButtons}
                </section>
            </>
        );
    }
}

export default formValidate({
    form: TICKETS_PAGE_FORM_NAME,
    validate,
})(TicketSelectionCompactSizePage);
